import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Briefcase, Users, Mail, Calendar, Trash2, Edit, Download, X, Package, Star, Building2, MessageSquarePlus, Handshake, GraduationCap, Shield, Key, ChevronDown, ChevronRight, UserCog } from "lucide-react";
import api, { API, tokenStorage } from "../../lib/api";
import { toast, Toaster } from "sonner";
import CMSPanel from "./CMSPanel";

// Hook: close on Escape — better UX for modals
function useEscapeKey(onClose) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
}

// ---------- Field schemas ----------
const PRODUCT_FIELDS = [
  { name: "name", label: "Name", type: "text", required: true },
  { name: "slug", label: "Slug (URL: /products/<slug>)", type: "text", required: true, placeholder: "codeflare-crm" },
  { name: "tagline", label: "Tagline", type: "text", required: true },
  { name: "category", label: "Category", type: "text" },
  { name: "description", label: "Description", type: "textarea" },
  { name: "features", label: "Features (one per line)", type: "list" },
  { name: "icon", label: "Icon (Sparkles, Database, Boxes, GraduationCap, Code2, Globe)", type: "text" },
  { name: "accent", label: "Accent color hex", type: "text", placeholder: "#FF6B1A" },
  { name: "hero_image", label: "Hero image", type: "image", ratio: "16:9 · max 8MB" },
  { name: "screenshots", label: "Product screenshots", type: "images", ratio: "16:9 · upload up to ~6 images" },
  { name: "pricing_tiers", label: "Pricing tiers (JSON array)", type: "json", help: 'e.g. [{"name":"Starter","price":"₹999","items":["Feature 1"]}]' },
  { name: "cta_text", label: "CTA text", type: "text" },
  { name: "cta_link", label: "CTA link", type: "text" },
  { name: "demo_url", label: "Demo URL", type: "text" },
  { name: "is_active", label: "Active (visible on site)", type: "checkbox" },
];

const CLIENT_FIELDS = [
  { name: "name", label: "Client name", type: "text", required: true },
  { name: "logo_url", label: "Logo", type: "image", ratio: "1:1 (square) · transparent PNG/SVG preferred" },
  { name: "website_url", label: "Website URL", type: "text" },
  { name: "country", label: "Country", type: "text" },
  { name: "industry", label: "Industry", type: "text" },
  { name: "project_type", label: "Project type", type: "select", options: ["domestic", "international", "govt"] },
  { name: "description", label: "Short description", type: "textarea" },
  { name: "image_url", label: "Cover image", type: "image", ratio: "4:3 · max 8MB" },
  { name: "is_featured", label: "Featured", type: "checkbox" },
  { name: "is_active", label: "Active", type: "checkbox" },
];

const TESTIMONIAL_FIELDS = [
  { name: "name", label: "Name", type: "text", required: true },
  { name: "role", label: "Role", type: "text" },
  { name: "company", label: "Company", type: "text" },
  { name: "photo_url", label: "Photo", type: "image", ratio: "1:1 (square) · max 8MB" },
  { name: "rating", label: "Rating (1-5)", type: "number" },
  { name: "quote", label: "Quote", type: "textarea", required: true },
  { name: "is_featured", label: "Featured", type: "checkbox" },
  { name: "is_active", label: "Active", type: "checkbox" },
];

const COLLABORATION_FIELDS = [
  { name: "name", label: "Company name", type: "text", required: true },
  { name: "logo_url", label: "Logo", type: "image", ratio: "1:1 or wide · transparent PNG/SVG preferred" },
  { name: "website_url", label: "Website URL", type: "text" },
  { name: "description", label: "Short description", type: "textarea" },
  { name: "is_active", label: "Active (visible on site)", type: "checkbox" },
];

const EDUCATION_FIELDS = [
  { name: "name", label: "Institute name", type: "text", required: true, placeholder: "Brainware University" },
  { name: "logo_url", label: "Logo", type: "image", ratio: "1:1 · max 8MB" },
  { name: "website_url", label: "Website URL", type: "text" },
  { name: "program", label: "Program / partnership type", type: "text", placeholder: "Internships & Placement" },
  { name: "description", label: "Description", type: "textarea" },
  { name: "image_url", label: "Cover image", type: "image", ratio: "16:9 · max 8MB" },
  { name: "is_active", label: "Active", type: "checkbox" },
];

// Tabs gated by role
const ALL_TABS = [
  { key: "stats", label: "Overview", icon: Briefcase, roles: ["super_admin", "hr", "sales"] },
  { key: "jobs", label: "Jobs", icon: Briefcase, roles: ["super_admin", "hr"] },
  { key: "applications", label: "Applications", icon: Users, roles: ["super_admin", "hr"] },
  { key: "inquiries", label: "Inquiries", icon: MessageSquarePlus, roles: ["super_admin", "sales"] },
  { key: "products", label: "Products", icon: Package, roles: ["super_admin"] },
  { key: "clients", label: "Clients", icon: Building2, roles: ["super_admin"] },
  { key: "collaborations", label: "Collaborations", icon: Handshake, roles: ["super_admin"] },
  { key: "education", label: "Education Partners", icon: GraduationCap, roles: ["super_admin"] },
  { key: "testimonials", label: "Testimonials", icon: Star, roles: ["super_admin"] },
  { key: "contacts", label: "Contacts", icon: Mail, roles: ["super_admin", "sales"] },
  { key: "meetings", label: "Meetings", icon: Calendar, roles: ["super_admin", "sales"] },
  { key: "users", label: "Users", icon: Shield, roles: ["super_admin"] },
];

const APPLICATION_STATUSES = ["new", "reviewed", "contacted", "rejected", "hired"];
const STATUS_COLOR = {
  new: "bg-blue-500/15 border-blue-500/40 text-blue-300",
  reviewed: "bg-zinc-500/15 border-zinc-500/40 text-zinc-300",
  contacted: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  rejected: "bg-red-500/15 border-red-500/40 text-red-300",
  hired: "bg-green-500/15 border-green-500/40 text-green-300",
};

const ROLE_LABELS = { super_admin: "Super Admin", hr: "HR", sales: "Sales", viewer: "Viewer" };
const ROLE_COLORS = {
  super_admin: "bg-[#FF6B1A]/15 border-[#FF6B1A]/40 text-[#FFAA33]",
  hr: "bg-blue-500/15 border-blue-500/40 text-blue-300",
  sales: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  viewer: "bg-zinc-500/15 border-zinc-500/40 text-zinc-300",
};

export default function AdminDashboard() {
  const [me, setMe] = useState(tokenStorage.getUser());
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [users, setUsers] = useState([]);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();

  const role = me?.role || "viewer";
  const tabs = useMemo(() => ALL_TABS.filter((t) => t.roles.includes(role)), [role]);

  const loadAll = useCallback(async () => {
    try {
      const calls = [api.get("/auth/me"), api.get("/admin/stats")];
      if (["super_admin", "hr"].includes(role)) {
        calls.push(api.get("/admin/jobs"));
        calls.push(api.get("/admin/applications"));
      } else { calls.push(Promise.resolve({ data: [] })); calls.push(Promise.resolve({ data: [] })); }
      if (["super_admin", "sales"].includes(role)) {
        calls.push(api.get("/admin/contacts"));
        calls.push(api.get("/admin/meetings"));
        calls.push(api.get("/admin/inquiries"));
      } else {
        calls.push(Promise.resolve({ data: [] }));
        calls.push(Promise.resolve({ data: [] }));
        calls.push(Promise.resolve({ data: [] }));
      }
      if (role === "super_admin") calls.push(api.get("/admin/users"));
      else calls.push(Promise.resolve({ data: [] }));

      const [meRes, s, j, a, c, m, q, u] = await Promise.all(calls);
      setMe(meRes.data); tokenStorage.setUser(meRes.data);
      setStats(s.data); setJobs(j.data); setApplications(a.data);
      setContacts(c.data); setMeetings(m.data); setInquiries(q.data); setUsers(u.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        tokenStorage.clear();
        navigate("/admin/login");
      }
    }
  }, [navigate, role]);

  useEffect(() => {
    if (!tokenStorage.get()) { navigate("/admin/login"); return; }
    loadAll();
  }, [loadAll, navigate]);

  // Make sure default tab is allowed for role
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find((t) => t.key === tab)) setTab(tabs[0].key);
  }, [tabs, tab]);

  const logout = () => {
    tokenStorage.clear();
    navigate("/admin/login");
  };

  const downloadFile = async (appId, fileType, originalName) => {
    try {
      const token = tokenStorage.get();
      const res = await fetch(`${API}/admin/applications/${appId}/file/${fileType}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = originalName || `${fileType}_${appId}`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("Download failed"); }
  };

  const deleteJob = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/admin/jobs/${id}`);
    toast.success("Job deleted");
    loadAll();
  };

  const updateAppStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      loadAll();
    } catch { toast.error("Failed to update"); }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Toaster theme="dark" position="top-center" />
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-xl font-bold tracking-wide">CODEFLARE <span className="flame-text">LABS</span></span>
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-widest hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowProfileModal(true)} data-testid="admin-profile-btn" className="px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-sm flex items-center gap-2">
              <UserCog size={14} className="text-[#FFAA33]" />
              <span className="font-semibold">{me?.name || me?.email || "Profile"}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${ROLE_COLORS[role] || ROLE_COLORS.viewer}`}>{ROLE_LABELS[role] || role}</span>
            </button>
            <button onClick={logout} className="px-4 py-2 rounded-full border border-white/10 hover:bg-white/5 text-sm flex items-center gap-2" data-testid="admin-logout-btn">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-4 py-2 rounded-full text-sm border flex items-center gap-2 ${tab === t.key ? "bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold border-transparent" : "border-white/10 text-zinc-300 hover:bg-white/5"}`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "stats" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4">
            {[
              { l: "Total Jobs", v: stats.jobs },
              { l: "Active Jobs", v: stats.active_jobs },
              { l: "Applications", v: stats.applications },
              { l: "Inquiries", v: stats.inquiries },
              { l: "Products", v: stats.products },
              { l: "Clients", v: stats.clients },
              { l: "Testimonials", v: stats.testimonials },
              { l: "Contacts", v: stats.contacts },
              { l: "Meetings", v: stats.meetings },
            ].map((s) => (
              <div key={s.l} className="glass rounded-2xl p-5">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500">{s.l}</p>
                <p className="text-3xl font-bold flame-text mt-2">{s.v}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "inquiries" && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Service & Product Inquiries ({inquiries.length})</h2>
            <div className="space-y-3">
              {inquiries.length === 0 && <p className="text-zinc-500">No inquiries yet.</p>}
              {inquiries.map((q) => (
                <div key={q.id} className="glass rounded-xl p-5" data-testid={`admin-inquiry-${q.id}`}>
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${q.inquiry_type === "product" ? "bg-blue-500/15 border-blue-500/40 text-blue-300" : "bg-orange-500/15 border-orange-500/40 text-orange-300"}`}>{q.inquiry_type}</span>
                        <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300">{q.status}</span>
                      </div>
                      <p className="font-semibold">{q.name} <span className="text-xs text-zinc-500">·</span> <span className="text-[#FFAA33] text-sm">{q.item_label}</span></p>
                      <p className="text-sm text-zinc-400 mt-1">{q.email} · {q.phone}{q.company && ` · ${q.company}`}</p>
                      {(q.budget || q.timeline) && (
                        <p className="text-xs text-zinc-500 mt-1">{q.budget && `Budget: ${q.budget}`}{q.budget && q.timeline && " · "}{q.timeline && `Timeline: ${q.timeline}`}</p>
                      )}
                      {q.message && <p className="mt-3 text-sm text-zinc-300 border-l-2 border-[#FF6B1A]/40 pl-3">{q.message}</p>}
                    </div>
                    <p className="text-xs text-zinc-500 shrink-0">{new Date(q.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "products" && (
          <CMSPanel title="Products" subtitle="Software products with their own landing page" endpoint="/admin/products" fields={PRODUCT_FIELDS} primary="name"
            defaults={{ is_active: true, icon: "Sparkles", accent: "#FF6B1A", cta_text: "Request Demo", cta_link: "/contact", features: [], screenshots: [], pricing_tiers: [] }} />
        )}
        {tab === "clients" && (
          <CMSPanel title="Clients" subtitle="Portfolio with website links" endpoint="/admin/clients" fields={CLIENT_FIELDS} primary="name"
            defaults={{ is_active: true, project_type: "domestic", country: "India" }} />
        )}
        {tab === "testimonials" && (
          <CMSPanel title="Testimonials" subtitle="Quotes shown on home page" endpoint="/admin/testimonials" fields={TESTIMONIAL_FIELDS} primary="name"
            defaults={{ rating: 5, is_active: true, is_featured: true }} />
        )}
        {tab === "collaborations" && (
          <CMSPanel title="Collaborations" subtitle="Partner companies shown on the home page" endpoint="/admin/collaborations" fields={COLLABORATION_FIELDS} primary="name"
            defaults={{ is_active: true }} />
        )}
        {tab === "education" && (
          <CMSPanel title="Education Partners" subtitle="Colleges & universities shown on the home page" endpoint="/admin/education-partners" fields={EDUCATION_FIELDS} primary="name"
            defaults={{ is_active: true }} />
        )}

        {tab === "jobs" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Job Postings</h2>
              <button onClick={() => { setEditingJob(null); setShowJobForm(true); }} className="px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold inline-flex items-center gap-2" data-testid="admin-new-job-btn">
                <Plus size={16} /> New Job
              </button>
            </div>
            <div className="space-y-3">
              {jobs.map((j) => (
                <div key={j.id} className="glass rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`admin-job-${j.id}`}>
                  <div className="flex-1">
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#FF6B1A]/10 border border-[#FF6B1A]/40 text-[#FFAA33]">{j.employment_type}</span>
                      {!j.is_active && <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/40 text-red-400">Inactive</span>}
                    </div>
                    <p className="font-semibold">{j.title}</p>
                    <p className="text-sm text-zinc-400">{j.location} · {j.salary_range || "—"}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditingJob(j); setShowJobForm(true); }} className="px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-sm flex items-center gap-1"><Edit size={12} /> Edit</button>
                    <button onClick={() => deleteJob(j.id)} className="px-3 py-2 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm flex items-center gap-1"><Trash2 size={12} /> Delete</button>
                  </div>
                </div>
              ))}
            </div>
            {showJobForm && (
              <JobForm initial={editingJob} onClose={() => setShowJobForm(false)} onSaved={() => { setShowJobForm(false); loadAll(); }} />
            )}
          </div>
        )}

        {tab === "applications" && (
          <ApplicationsByJob jobs={jobs} applications={applications} onStatus={updateAppStatus} onDownload={downloadFile} />
        )}

        {tab === "contacts" && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Contact Enquiries ({contacts.length})</h2>
            <div className="space-y-3">
              {contacts.length === 0 && <p className="text-zinc-500">No enquiries yet.</p>}
              {contacts.map((c) => (
                <div key={c.id} className="glass rounded-xl p-5">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-semibold">{c.name}</p>
                      <p className="text-sm text-zinc-400">{c.email}{c.phone && ` · ${c.phone}`}</p>
                      {c.subject && <p className="text-sm mt-1 text-[#FFAA33]">{c.subject}</p>}
                    </div>
                    <p className="text-xs text-zinc-500">{new Date(c.created_at).toLocaleString()}</p>
                  </div>
                  <p className="mt-3 text-sm text-zinc-300">{c.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "meetings" && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Scheduled Meetings ({meetings.length})</h2>
            <div className="space-y-3">
              {meetings.length === 0 && <p className="text-zinc-500">No meetings scheduled yet — chat with Flare to book one!</p>}
              {meetings.map((m) => (
                <div key={m.id} className="glass rounded-xl p-5">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <p className="font-semibold">{m.name} <span className="text-xs text-zinc-500">· {m.topic}</span></p>
                      <p className="text-sm text-zinc-400">{m.email}{m.phone && ` · ${m.phone}`}{m.company && ` · ${m.company}`}</p>
                      <p className="text-sm mt-1 text-[#FFAA33]">{m.preferred_date} · {m.duration_minutes} min</p>
                      {m.notes && <p className="mt-2 text-xs text-zinc-400">{m.notes}</p>}
                    </div>
                    <span className="px-2.5 py-1 rounded-full bg-green-500/15 border border-green-500/30 text-green-300 text-[10px] uppercase tracking-widest">{m.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "users" && role === "super_admin" && (
          <UsersPanel users={users} reload={loadAll} meEmail={me?.email} />
        )}
      </div>

      {showProfileModal && (
        <ProfileModal me={me} onClose={() => setShowProfileModal(false)} />
      )}
    </div>
  );
}

// ============== Applications grouped by Job ==============
function ApplicationsByJob({ jobs, applications, onStatus, onDownload }) {
  const [openJob, setOpenJob] = useState(null);
  const [filter, setFilter] = useState({}); // jobId -> status

  const grouped = useMemo(() => {
    const map = {};
    for (const a of applications) {
      const jid = a.job_id || "unknown";
      if (!map[jid]) map[jid] = [];
      map[jid].push(a);
    }
    return map;
  }, [applications]);

  // Build a list of jobs that includes those that have applications even if job is missing
  const allJobIds = useMemo(() => {
    const known = new Set(jobs.map((j) => j.id));
    const extras = Object.keys(grouped).filter((id) => !known.has(id));
    return [...jobs, ...extras.map((id) => ({ id, title: grouped[id][0]?.job_title || "Unknown role", employment_type: "—", location: "—", _orphan: true }))];
  }, [jobs, grouped]);

  return (
    <div data-testid="applications-by-job">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold">Applications by Role <span className="text-zinc-500 text-base">({applications.length})</span></h2>
      </div>
      {applications.length === 0 && <p className="text-zinc-500">No applications yet.</p>}

      <div className="space-y-3">
        {allJobIds.map((j) => {
          const apps = grouped[j.id] || [];
          if (apps.length === 0 && !j._orphan) {
            // Show jobs without applications too — collapsible empty state
          }
          const status = filter[j.id] || "all";
          const filtered = status === "all" ? apps : apps.filter((a) => (a.status || "new") === status);
          const counts = APPLICATION_STATUSES.reduce((acc, s) => { acc[s] = apps.filter((a) => (a.status || "new") === s).length; return acc; }, { all: apps.length });
          const isOpen = openJob === j.id;
          return (
            <div key={j.id} className="glass rounded-2xl overflow-hidden" data-testid={`job-applications-${j.id}`}>
              <button
                type="button"
                onClick={() => setOpenJob(isOpen ? null : j.id)}
                className="w-full flex items-center gap-3 p-5 text-left hover:bg-white/5"
              >
                {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#FF6B1A]/10 border border-[#FF6B1A]/40 text-[#FFAA33]">{j.employment_type}</span>
                    {j._orphan && <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-zinc-500/10 border border-zinc-500/40 text-zinc-300">archived</span>}
                  </div>
                  <p className="font-semibold">{j.title}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{j.location} · {apps.length} applicant{apps.length === 1 ? "" : "s"}</p>
                </div>
                <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300">{apps.length}</span>
              </button>

              {isOpen && (
                <div className="border-t border-white/5 p-5 space-y-4">
                  {/* Status filter chips */}
                  <div className="flex flex-wrap gap-2">
                    {["all", ...APPLICATION_STATUSES].map((s) => (
                      <button key={s} onClick={() => setFilter((f) => ({ ...f, [j.id]: s }))}
                        data-testid={`filter-${j.id}-${s}`}
                        className={`text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${status === s ? "bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold border-transparent" : "bg-white/5 border-white/10 text-zinc-300 hover:bg-white/10"}`}>
                        {s} ({counts[s] || 0})
                      </button>
                    ))}
                  </div>

                  {filtered.length === 0 && <p className="text-zinc-500 text-sm">No applicants match this filter.</p>}
                  <div className="space-y-3">
                    {filtered.map((a) => (
                      <div key={a.id} className="rounded-xl bg-white/5 border border-white/10 p-5" data-testid={`admin-app-${a.id}`}>
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${STATUS_COLOR[a.status || "new"]}`}>{a.status || "new"}</span>
                            </div>
                            <p className="font-semibold">{a.name}</p>
                            <p className="text-sm text-zinc-400">{a.email} · {a.phone}</p>
                            <p className="text-xs text-zinc-500 mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
                            {a.cover_letter && <p className="mt-3 text-sm text-zinc-300 border-l-2 border-[#FF6B1A]/40 pl-3">{a.cover_letter}</p>}
                          </div>
                          <div className="flex flex-col gap-2 md:items-end">
                            <select value={a.status || "new"} onChange={(e) => onStatus(a.id, e.target.value)}
                              data-testid={`status-select-${a.id}`}
                              className="rounded-full bg-white/5 border border-white/10 px-3 py-2 text-xs">
                              {APPLICATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <div className="flex flex-wrap gap-2">
                              <button onClick={() => onDownload(a.id, "photo", a.photo?.filename)} className="px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-xs flex items-center gap-1"><Download size={12} /> Photo</button>
                              <button onClick={() => onDownload(a.id, "resume", a.resume?.filename)} className="px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-xs flex items-center gap-1"><Download size={12} /> Resume</button>
                              <button onClick={() => onDownload(a.id, "govt_id", a.govt_id?.filename)} className="px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-xs flex items-center gap-1"><Download size={12} /> Govt ID</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============== Users management (super_admin) ==============
function UsersPanel({ users, reload, meEmail }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);

  const remove = async (id, email) => {
    if (email === meEmail) { toast.error("You cannot delete your own account"); return; }
    if (!window.confirm(`Delete user ${email}?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold">Users & Permissions</h2>
          <p className="text-sm text-zinc-500 mt-1">Roles: <span className="text-[#FFAA33]">Super Admin</span> (full access), <span className="text-blue-300">HR</span> (jobs + applications), <span className="text-emerald-300">Sales</span> (inquiries, contacts, meetings), <span className="text-zinc-300">Viewer</span> (overview only).</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold inline-flex items-center gap-2" data-testid="admin-new-user-btn">
          <Plus size={16} /> New User
        </button>
      </div>
      <div className="space-y-3">
        {users.map((u) => (
          <div key={u.id} className="glass rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid={`admin-user-${u.id}`}>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${ROLE_COLORS[u.role]}`}>{ROLE_LABELS[u.role] || u.role}</span>
                {!u.is_active && <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/40 text-red-400">Disabled</span>}
                {u.email === meEmail && <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300">You</span>}
              </div>
              <p className="font-semibold">{u.name}</p>
              <p className="text-xs text-zinc-500">{u.email}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => { setEditing(u); setShowForm(true); }} className="px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-xs flex items-center gap-1" data-testid={`edit-user-${u.id}`}><Edit size={12} /> Edit</button>
              <button onClick={() => setResetting(u)} className="px-3 py-2 rounded-full border border-white/10 hover:bg-white/5 text-xs flex items-center gap-1" data-testid={`reset-pw-${u.id}`}><Key size={12} /> Reset Password</button>
              <button onClick={() => remove(u.id, u.email)} className="px-3 py-2 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs flex items-center gap-1"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>

      {showForm && <UserForm initial={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reload(); }} />}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onDone={() => { setResetting(null); reload(); }} />}
    </div>
  );
}

function UserForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? { name: initial.name, email: initial.email, role: initial.role, is_active: initial.is_active } : { name: "", email: "", role: "viewer", password: "", is_active: true });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial) {
        await api.put(`/admin/users/${initial.id}`, { name: form.name, role: form.role, is_active: form.is_active });
        toast.success("Saved");
      } else {
        if ((form.password || "").length < 6) { toast.error("Password must be at least 6 chars"); setSaving(false); return; }
        await api.post("/admin/users", { name: form.name, email: form.email, role: form.role, password: form.password });
        toast.success("User created");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-y-auto" data-testid="admin-user-form">
      <div className="relative w-full max-w-md glass rounded-3xl p-7 my-8">
        <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full hover:bg-white/10"><X size={18} /></button>
        <h3 className="text-2xl font-bold mb-5">{initial ? "Edit user" : "New user"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          {!initial && <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />}
          {!initial && <Field label="Initial password (min 6)" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required />}
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm">
              <option value="super_admin">Super Admin — full access</option>
              <option value="hr">HR — Jobs &amp; Applications</option>
              <option value="sales">Sales — Inquiries, Contacts, Meetings</option>
              <option value="viewer">Viewer — read-only overview</option>
            </select>
          </div>
          {initial && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Account active
            </label>
          )}
          <button disabled={saving} type="submit" className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold disabled:opacity-50">{saving ? "Saving..." : "Save"}</button>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Min 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/password`, { new_password: pw });
      toast.success("Password updated");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="relative w-full max-w-md glass rounded-3xl p-7" data-testid="reset-password-modal">
        <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full hover:bg-white/10"><X size={18} /></button>
        <h3 className="text-2xl font-bold mb-1">Reset password</h3>
        <p className="text-sm text-zinc-400 mb-5">For <span className="text-white">{user.name}</span> ({user.email})</p>
        <form onSubmit={submit} className="space-y-3">
          <Field label="New password" type="password" value={pw} onChange={setPw} required />
          <button disabled={saving} type="submit" className="w-full py-3 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold disabled:opacity-50">{saving ? "Saving..." : "Reset password"}</button>
        </form>
      </div>
    </div>
  );
}

function ProfileModal({ me, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("Min 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/me/password", { current_password: current, new_password: next });
      toast.success("Password updated");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="relative w-full max-w-md glass rounded-3xl p-7" data-testid="profile-modal">
        <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full hover:bg-white/10"><X size={18} /></button>
        <h3 className="text-2xl font-bold mb-1">Your profile</h3>
        <p className="text-sm text-zinc-400 mb-5">{me?.name} <span className="text-zinc-600">·</span> {me?.email} <span className="text-zinc-600">·</span> <span className="text-[#FFAA33]">{ROLE_LABELS[me?.role] || me?.role}</span></p>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Current password" type="password" value={current} onChange={setCurrent} required />
          <Field label="New password" type="password" value={next} onChange={setNext} required />
          <Field label="Confirm new password" type="password" value={confirm} onChange={setConfirm} required />
          <button disabled={saving} type="submit" className="w-full py-3 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold disabled:opacity-50" data-testid="change-password-submit">{saving ? "Saving..." : "Change password"}</button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-zinc-400">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A]" />
    </div>
  );
}

function JobForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(
    initial || { title: "", employment_type: "full-time", location: "", description: "", requirements: "", salary_range: "", is_active: true }
  );
  const [saving, setSaving] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      requirements: typeof form.requirements === "string" ? form.requirements.split("\n").filter(Boolean) : form.requirements,
    };
    try {
      if (initial) await api.put(`/admin/jobs/${initial.id}`, payload);
      else await api.post("/admin/jobs", payload);
      toast.success("Saved!");
      onSaved();
    } catch (err) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 overflow-y-auto" data-testid="admin-job-form">
      <div className="relative w-full max-w-xl glass rounded-3xl p-8 my-8">
        <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full hover:bg-white/10"><X size={18} /></button>
        <h3 className="text-2xl font-bold mb-5">{initial ? "Edit" : "New"} Job</h3>
        <form onSubmit={onSubmit} className="space-y-3">
          <BasicInput label="Title" name="title" value={form.title} onChange={onChange} required />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-400">Type</label>
              <select name="employment_type" value={form.employment_type} onChange={onChange} className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm">
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="internship">Internship</option>
              </select>
            </div>
            <BasicInput label="Location" name="location" value={form.location} onChange={onChange} required />
          </div>
          <BasicInput label="Salary range (optional)" name="salary_range" value={form.salary_range || ""} onChange={onChange} />
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400">Description</label>
            <textarea name="description" value={form.description} onChange={onChange} rows={3} required className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-400">Requirements (one per line)</label>
            <textarea name="requirements" value={Array.isArray(form.requirements) ? form.requirements.join("\n") : form.requirements} onChange={onChange} rows={4} className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="is_active" checked={form.is_active} onChange={onChange} /> Active (visible publicly)
          </label>
          <button disabled={saving} type="submit" className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black font-semibold disabled:opacity-50">
            {saving ? "Saving..." : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
}

function BasicInput({ label, ...props }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-zinc-400">{label}</label>
      <input {...props} className="mt-1.5 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#FF6B1A]" />
    </div>
  );
}
